import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getAllReservations } from '../utils/firebaseUtils';

const WEEKDAY_NAMES = ['Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis', 'Sekmadienis'];
const WEEKDAY_SHORT = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

// Full day 00:00–23:00 by hour (each row = one hour, e.g. 7:00 = 7:00–8:00)
const getHourTimeSlots = () => {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    slots.push(`${h.toString().padStart(2, '0')}:00`);
  }
  return slots;
};

// Normalize startTime "HH:mm" or "HH:mm:ss" to hour slot "HH:00"
const startTimeToHourSlot = (startTime) => {
  if (!startTime) return null;
  const match = String(startTime).match(/^(\d{1,2})/);
  const hour = match ? parseInt(match[1], 10) : null;
  if (hour == null || hour < 0 || hour > 23) return null;
  return `${hour.toString().padStart(2, '0')}:00`;
};

// Monday = 0 (JS getDay: 0=Sun, 1=Mon, ... → (getDay()+6)%7)
const getWeekdayIndex = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return (d.getDay() + 6) % 7;
};

// White (no bookings) → yellow (in between) → red (most booked)
const heatColor = (ratio) => {
  if (ratio <= 0) return 'rgb(255, 255, 255)';
  if (ratio >= 1) return 'rgb(220, 53, 69)';
  if (ratio <= 0.5) {
    const t = ratio * 2; // 0..1 over first half
    return `rgb(255, ${255 - Math.round(t * 20)}, ${255 - Math.round(t * 196)})`; // white → yellow
  }
  const t = (ratio - 0.5) * 2; // 0..1 over second half
  return `rgb(${255 - Math.round(t * 35)}, ${235 - Math.round(t * 182)}, ${59 + Math.round(t * 10)})`; // yellow → red
};

export default function AdminStatistikaPage() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getAllReservations()
      .then((list) => {
        if (cancelled) return;
        setReservations(list.filter((r) => r.status !== 'cancelled' && r.coachId !== 'preview'));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Nepavyko užkrauti rezervacijų.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const timeSlots = useMemo(() => getHourTimeSlots(), []);

  const { counts, maxCount } = useMemo(() => {
    const map = {}; // key: "weekdayIndex_HH:00"
    let max = 0;
    reservations.forEach((r) => {
      const date = r.date || r.lessonDate;
      const startTime = r.startTime || r.lessonStartTime || '';
      if (!date || !startTime) return;
      const hourSlot = startTimeToHourSlot(startTime);
      if (!hourSlot) return;
      const weekday = getWeekdayIndex(date);
      const key = `${weekday}_${hourSlot}`;
      map[key] = (map[key] || 0) + 1;
      if (map[key] > max) max = map[key];
    });
    return { counts: map, maxCount: max };
  }, [reservations]);

  const getCount = (weekdayIndex, time) => counts[`${weekdayIndex}_${time}`] || 0;

  const cellHeight = 28;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Times most booked
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When lessons are scheduled (by lesson day and time). Confirmed reservations only; preview/seed account excluded. White = none, yellow = some, red = most.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          maxWidth: 960,
          mx: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Week view (00:00–24:00, by hour)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total reservations: {reservations.length}. Max in one hour slot: {maxCount}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          {/* Time column */}
          <Box
            sx={{
              width: 56,
              flexShrink: 0,
              borderRight: '2px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Box
              sx={{
                height: 48,
                borderBottom: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Time
              </Typography>
            </Box>
            {timeSlots.map((time, idx) => (
                <Box
                  key={time}
                  sx={{
                    height: cellHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                  }}
                >
                  {time}
                </Box>
              ))}
          </Box>

          {/* Day columns */}
          {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
            <Box
              key={dayIndex}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRight: dayIndex < 6 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  height: 48,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  px: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {WEEKDAY_SHORT[dayIndex]}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                  {WEEKDAY_NAMES[dayIndex]}
                </Typography>
              </Box>
              {timeSlots.map((time) => {
                const count = getCount(dayIndex, time);
                const ratio = maxCount > 0 ? count / maxCount : 0;
                const bgColor = heatColor(ratio);
                const textColor = ratio > 0.4 ? 'white' : ratio > 0 ? 'grey.900' : 'grey.500';
                return (
                  <Box
                    key={`${dayIndex}-${time}`}
                    sx={{
                      height: cellHeight,
                      bgcolor: bgColor,
                      color: textColor,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {count > 0 ? count : '—'}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Legend */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            alignItems: 'center',
            px: 2,
            py: 2,
            borderTop: '2px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: 'rgb(255,255,255)', border: '1px solid', borderColor: 'divider' }} />
            <Typography variant="body2" fontWeight={500}>None</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: 'rgb(255, 235, 59)' }} />
            <Typography variant="body2" fontWeight={500}>Some</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: 'rgb(220, 53, 69)' }} />
            <Typography variant="body2" fontWeight={500}>Most</Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
